// Dropdown of the project's analytics data-source connections, with a
// "Manage…" button opening the BiConnectionsPanel. Replaces free-text
// CONNECTION_ID inputs in the Schema / Business Area designers. The empty
// value means "(Project database)" — today's default behavior.

import { useCallback, useEffect, useState } from 'react'
import { listBiConnections, type BiConnection } from '@/api/connections'
import BiConnectionsPanel from './BiConnectionsPanel'

const selectStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '9px 11px',
  fontSize: 15,
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  background: 'var(--color-input-bg, var(--color-bg))',
  color: 'var(--color-text)',
  cursor: 'pointer',
}

export default function ConnectionPicker({
  project, value, onChange, disabled, defaultLabel = '(Project database)',
}: {
  project: string
  value: string
  onChange: (connectionId: string) => void
  disabled?: boolean
  defaultLabel?: string
}) {
  const [connections, setConnections] = useState<BiConnection[]>([])
  const [manageOpen, setManageOpen] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setConnections(await listBiConnections(project))
    } catch {
      setConnections([])
    }
  }, [project])

  useEffect(() => { void refresh() }, [refresh])

  const known = connections.some((c) => c.CONNECTION_ID === value)

  return (
    <>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select
          style={selectStyle}
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{defaultLabel}</option>
          {!known && value && (
            <option value={value}>{value} (not found — save it in Manage…)</option>
          )}
          {connections.map((c) => (
            <option key={c.CONNECTION_ID} value={c.CONNECTION_ID}>
              {c.CONNECTION_NAME || c.CONNECTION_ID}
              {c.DATABASE_TYPE ? ` — ${c.DATABASE_TYPE}` : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setManageOpen(true)}
          title="Add, edit or test analytics data sources"
          style={{
            background: 'transparent', border: 'none', color: 'var(--color-accent)',
            font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            whiteSpace: 'nowrap', padding: '4px 2px',
          }}
        >
          Manage…
        </button>
      </div>
      <BiConnectionsPanel
        project={project}
        variant="modal"
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onChanged={() => void refresh()}
      />
    </>
  )
}
